import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import { type CreateManualPaymentPayload, type TitlePayment } from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Gerencia o ledger de pagamentos de um caso (Task 18): lista os lançamentos, adiciona um manual e
// remove um manual. Mesmo padrão do useLegalCaseDeposits (re-seed por caso, guarda de corrida por
// geração de request) — o ledger atualiza sem recarregar o page-view inteiro.
export default function useLegalCasePayments(
    caseId: string | null,
    initialPayments?: TitlePayment[],
) {
    const [payments, setPayments] = useState<TitlePayment[]>(initialPayments || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const seededCaseRef = useRef<string | null>(null);
    const fetchGenRef = useRef(0);

    const fetchPayments = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getPayments(caseId);
            if (fetchGenRef.current !== generation) return;
            setPayments(result.items || []);
        } catch {
            // silencioso — o valor pago já aparece no bloco de Valores da pretensão
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setPayments(initialPayments || []);
        setErrorFields([]);
        fetchPayments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    const clearErrors = () => setErrorFields([]);

    const addPayment = async (data: CreateManualPaymentPayload): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addPayment(caseId, data);
            await fetchPayments();
            toast.success('Pagamento lançado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso ou título não encontrado');
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao lançar pagamento');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removePayment = async (paymentId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removePayment(caseId, paymentId);
            await fetchPayments();
            toast.success('Lançamento removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover lançamento');
        } finally {
            setIsSaving(false);
        }
    };

    return { payments, isLoading, isSaving, errorFields, addPayment, removePayment, clearErrors };
}
